import { useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { api } from 'helpers/axios';
import { errorMessage } from 'helpers/common';

// One page of the employee pool behind the action selectors; the `lite` endpoint skips
// relations the selector doesn't show. `onTotal(total)` runs only for unsearched responses,
// so it reports the filter's match count. A response for outdated inputs is ignored.
export default function useEmployeePool({ filters, page, perPage, search, enabled = true, onTotal }) {
  const [employees, setEmployees] = useState(null);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1, perPage: 20, currentPage: 1 });
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false); // true after the first response
  const [error, setError] = useState(null);

  // Kept in a ref so an inline callback does not refetch on every render.
  const onTotalRef = useRef(onTotal);
  onTotalRef.current = onTotal;

  useEffect(() => {
    if (!enabled) return;
    let active = true;

    const fetchPool = async () => {
      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams({
          limit: String(perPage),
          page: String(page),
          sort: 'first_name:asc',
          lite: '1',
        });
        if (search) params.set('search', search);

        const { data, status } = await api.post(`/employees?${params.toString()}`, { filters: filters || [] });
        if (!active || status !== 200) return;

        const list = (data?.data || []).map(item => ({
          hash_id: item.hash_id,
          first_name: item.first_name,
          last_name: item.last_name,
          employee_number: item.employee_number,
        }));
        const p = data?.meta?.pagination;
        const total = p?.total ?? list.length;

        setEmployees(list);
        setPagination({
          total,
          totalPages: p?.total_pages ?? 1,
          perPage: p?.per_page ?? perPage,
          currentPage: p?.current_page ?? page,
        });
        if (!search) onTotalRef.current?.(total);
      } catch (err) {
        if (!active) return;
        setError(errorMessage(err, 'fetch employees for action'));
        Sentry.captureException(err);
      } finally {
        if (active) {
          setLoading(false);
          setLoaded(true);
        }
      }
    };

    fetchPool();
    return () => { active = false; };
  }, [filters, page, perPage, search, enabled]);

  return { employees, pagination, loading, loaded, error };
}
